import { useCallback, useMemo } from 'react';
import type { SearchEntityType } from '@/entities/search';
import { useSearchInfiniteQuery } from '@/entities/search';
import { useSmartSearchState } from './useSmartSearchState';
import { useCommittedSearch } from './useCommittedSearch';
import { usePredicateSync } from './usePredicateSync';
import { buildSearchRequestParams, buildSearchQueryParams } from './buildSearchRequest';

export type UseSmartSearchInfiniteParams<
  EntityType extends SearchEntityType,
  Filters extends Record<string, unknown> = Record<string, unknown>,
> = {
  entityType: EntityType;
  /** Base filters merged into request (e.g. { isDeleted: false }) */
  baseFilters?: Filters;
  pageSize?: number;
  /**
   * Морфологический полнотекстовый поиск по смыслу (top-level поле запроса, только knowledge/knowledge_set).
   * Реактивен: применяется без submit и сочетается по «И» с предикатом и фильтрами. Пустая строка условия не накладывает
   */
  search?: string;
};

/**
 * Smart search + infinite scroll.
 * Search is committed on explicit submit; changing committed triggers new query and resets pages.
 * Predicate info is synced back to search input.
 */
export const useSmartSearchInfinite = <
  EntityType extends SearchEntityType,
  Item,
  Filters extends Record<string, unknown> = Record<string, unknown>,
>(
  params: UseSmartSearchInfiniteParams<EntityType, Filters>,
) => {
  const { entityType, baseFilters = {} as Filters, pageSize = 50, search } = params;

  const searchState = useSmartSearchState();
  const { committed, commit, clear: clearCommitted } = useCommittedSearch();

  const queryParams = useMemo(() => buildSearchQueryParams(committed), [committed]);
  const searchRequest = useMemo(() => {
    const base = buildSearchRequestParams(entityType, queryParams);
    const trimmedSearch = search?.trim();
    return {
      ...base,
      // Поиск по смыслу — top-level поле запроса, а не фильтр
      ...(trimmedSearch ? { search: trimmedSearch } : {}),
      filters: { ...(base.filters ?? {}), ...baseFilters },
      pageSize,
    };
  }, [entityType, queryParams, baseFilters, pageSize, search]);

  const { data, isLoading, fetchNextPage, hasNextPage } = useSearchInfiniteQuery<Item>(searchRequest);

  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const predicateInfo = data?.pages[0]?.predicate ?? null;

  usePredicateSync({
    predicateInfo,
    committedSearch: committed,
    onSearchChange: searchState.onChange,
  });

  const handleSearch = useCallback(() => {
    const query = searchState.searchMode === 'nl' ? searchState.value.human.trim() : searchState.value.dsl.trim();
    commit(query, searchState.searchMode);
  }, [searchState.searchMode, searchState.value, commit]);

  const handleClear = useCallback(() => {
    searchState.clearSearch();
    clearCommitted();
  }, [searchState, clearCommitted]);

  return {
    searchState,
    items,
    isLoading,
    fetchNextPage,
    hasNextPage,
    predicateInfo,
    handleSearch,
    handleClear,
  };
};
